/**
 * cobblestone.ts — the landing's cobblestone wall, generated as inline SVG.
 *
 * There is no asset pipeline for images and nothing may 404 on a fresh deploy
 * (AGENTS.md), so the wall is made the way the sounds are: from first principles
 * at runtime. SVG rather than canvas because the layout is then a pure function
 * of the seed, testable in node where there is no DOM.
 *
 * **One wall, not a repeating tile.** A tile was the obvious approach and it does
 * not work. Cobble needs a half-offset bond, which necessarily puts stones across
 * the tile edge; each tile is rasterized independently, so both clipped halves get
 * antialiased against the mortar behind them and every repeat shows a dark line
 * where the two meet. Measured at 40/255 on a 360px repeat — clearly visible, and
 * not fixable by choosing a better size, because the stones cross the seam
 * diagonally at fractional coordinates whatever the size is. One oversized wall
 * scaled to cover has no interior edges, so the problem cannot arise.
 *
 * Inline SVG in the DOM rather than a CSS `url()` data URL for the same
 * no-surprises reason: no percent-encoding (which triples a points list of spaces
 * and commas), and no `%` or `#` in the markup that a URI parser might eat.
 *
 * Two things make it read as cobble rather than as scattered pebbles:
 *
 * - **Hexagonal packing.** Rows are offset half a stone, so the tight packing is
 *   the hex one: rows sit `sqrt(3)/2` of a column pitch apart, not a whole one.
 *   Equal pitches leave the diagonal neighbours 1.12 pitches away and the wall
 *   turns into polka dots.
 * - **Stones that nearly touch.** Radius is just over half a pitch so the flats
 *   meet and only the corners stay open. Mortar is a dark grey, not the void.
 */

export interface CobbleOptions {
  /** Nominal wall width. Scaled to cover the viewport, so it is not a pixel size. */
  width?: number;
  /** Nominal wall height. */
  height?: number;
  /** Stones across the nominal width. */
  columns?: number;
  /** Any integer. The same seed always yields the same wall. */
  seed?: number;
}

export interface Stone {
  /** Closed polygon outline. Edge stones fall outside the wall and get clipped. */
  points: readonly { x: number; y: number }[];
  /** Fill lightness in the grey ramp, 0..100 as CSS hsl wants it. */
  lightness: number;
}

/** 3:2-ish, so it covers both landscape and portrait without much waste. */
const DEFAULTS = { width: 1500, height: 950, columns: 24, seed: 7 } as const;

/**
 * Distinct stone shades. Stones are grouped by shade so `fill` and `stroke` are
 * written once per group instead of once per stone, which is most of the markup —
 * the whole wall is inlined into the page, so its size is worth caring about.
 *
 * Grouping means stones paint in shade order rather than in layout order. They
 * only just touch, so which of two neighbours wins the overlapping pixel is not
 * something you can see.
 */
const SHADES = 12;

/** Row pitch as a fraction of the column pitch: tight hexagonal packing. */
const HEX = Math.sqrt(3) / 2;

/** Points per stone. Eight reads as a rounded cobble; fewer looks like gravel. */
const FACETS = 8;

/** Stone radius over column pitch. Just past 0.5 so neighbouring flats meet. */
const FILL = 0.53;

/**
 * mulberry32 — small, fast, and good enough that neighbouring stones do not
 * visibly correlate. Deterministic so the wall is the same on every load.
 */
function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Every stone in the wall, in draw order.
 *
 * The loops start at -1 and run one past the end so the border stones are cut off
 * by the viewBox instead of stopping short of it — a wall that ends on a tidy
 * stone boundary has a bare mortar margin down each side.
 */
export function cobbleStones(options: CobbleOptions = {}): Stone[] {
  const { width, height, columns, seed } = { ...DEFAULTS, ...options };
  const random = rng(seed);
  const colPitch = width / columns;
  const rowPitch = colPitch * HEX;
  const rows = Math.ceil(height / rowPitch);
  const stones: Stone[] = [];

  for (let row = -1; row <= rows; row += 1) {
    // Every other row shifts half a stone — the bond that makes it cobble and
    // not tile.
    const offset = (Math.abs(row) % 2) * (colPitch / 2);
    for (let col = -1; col <= columns; col += 1) {
      const cx = (col + 0.5) * colPitch + offset;
      const cy = (row + 0.5) * rowPitch;
      stones.push(carve(cx, cy, colPitch, random));
    }
  }
  return stones;
}

/** One irregular cobble: a jittered ring of points about the cell centre. */
function carve(cx: number, cy: number, pitch: number, random: () => number): Stone {
  const points: { x: number; y: number }[] = [];
  // Nudge the whole stone off its lattice point so the grid does not read as one.
  const dx = (random() - 0.5) * pitch * 0.11;
  const dy = (random() - 0.5) * pitch * 0.11;
  for (let i = 0; i < FACETS; i += 1) {
    const angle = (i / FACETS) * Math.PI * 2 + (random() - 0.5) * 0.3;
    // Narrow jitter: stones stay chunky. Wide jitter reads as gravel again.
    const radius = pitch * FILL * (0.88 + random() * 0.12);
    // Whole units. The wall is scaled up to cover the viewport, so a sub-unit
    // coordinate buys nothing on a deliberately rough shape and costs four
    // characters in a page-inlined string.
    points.push({
      x: Math.round(cx + dx + Math.cos(angle) * radius),
      y: Math.round(cy + dy + Math.sin(angle) * radius),
    });
  }
  // Dark stone, with a wide spread so neighbours separate sharply rather than
  // blending into one grey mass. The floor is what keeps the wall dark; the
  // spread is what keeps it legible as individual stones.
  const shade = Math.floor(random() * SHADES);
  return { points, lightness: 19 + Math.round((shade / (SHADES - 1)) * 17) };
}

/**
 * The wall as an `<svg>` element, to be dropped straight into the DOM.
 *
 * `slice` is the SVG spelling of `background-size: cover`, so the caller only has
 * to give the element a size and the wall fills it at any aspect ratio.
 */
export function cobbleSvg(options: CobbleOptions = {}): string {
  const { width, height } = { ...DEFAULTS, ...options };
  const stones = cobbleStones(options);

  // One group per shade, so the colour attributes are written 12 times and not
  // once per stone. The lighter stroke is the lit top of the cobble — cheaper
  // than a bevel per stone and, under the torchlight, it reads much the same.
  const byShade = new Map<number, Stone[]>();
  for (const stone of stones) {
    const group = byShade.get(stone.lightness);
    if (group === undefined) byShade.set(stone.lightness, [stone]);
    else group.push(stone);
  }

  const body = [...byShade.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([lightness, group]) => {
      const shapes = group
        .map((s) => `<polygon points="${s.points.map((p) => `${p.x},${p.y}`).join(' ')}"/>`)
        .join('');
      // A dark outline rather than a light one: it cuts each stone away from its
      // neighbours instead of softening the join, which is what "sharper" means
      // at this size. The mortar behind supplies the depth.
      return (
        `<g fill="hsl(30 6% ${lightness}%)" stroke="hsl(28 9% ${Math.max(4, lightness - 12)}%)"` +
        ` stroke-width="1.4">${shapes}</g>`
      );
    })
    .join('');

  // Mortar: darker than the darkest stone so the gaps read as depth.
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}"` +
    ` preserveAspectRatio="xMidYMid slice" width="100%" height="100%">` +
    `<rect width="${width}" height="${height}" fill="hsl(28 7% 8%)"/>` +
    body +
    `</svg>`
  );
}

function round(n: number): number {
  return Math.round(n * 10) / 10;
}
